import { DollarSign, MessageSquare, Lock, Unlock, Gift, RotateCcw, Clock, Wallet, Timer, TrendingUp } from 'lucide-react';
import { money, pct, respTime } from './TimeframeSelect';

const ACCENTS = {
  blue: ['border-blue-500/20', 'bg-blue-500/10 text-blue-500'],
  green: ['border-green-500/20', 'bg-green-500/10 text-green-500'],
  emerald: ['border-emerald-500/20', 'bg-emerald-500/10 text-emerald-500'],
  pink: ['border-pink-500/20', 'bg-pink-500/10 text-pink-500'],
  red: ['border-red-500/20', 'bg-red-500/10 text-red-500'],
  sky: ['border-sky-500/20', 'bg-sky-500/10 text-sky-500'],
  amber: ['border-amber-500/20', 'bg-amber-500/10 text-amber-500'],
  violet: ['border-violet-500/20', 'bg-violet-500/10 text-violet-500'],
  cyan: ['border-cyan-500/20', 'bg-cyan-500/10 text-cyan-500'],
  orange: ['border-orange-500/20', 'bg-orange-500/10 text-orange-500'],
};

const Card = ({ label, value, sub, icon: Icon, accent = 'blue', testId, big }) => (
  <div className={`bg-card p-5 rounded-md border ${ACCENTS[accent][0]}`} data-testid={testId}>
    <div className="flex items-center justify-between mb-3">
      <span className="text-xs uppercase tracking-wider text-zinc-400 font-semibold">{label}</span>
      <div className={`${ACCENTS[accent][1]} p-2 rounded-md`}>
        <Icon className="w-4 h-4" strokeWidth={1.5} />
      </div>
    </div>
    <div className={`${big ? 'text-4xl md:text-5xl' : 'text-2xl md:text-3xl'} font-bold font-heading tracking-tighter tabular-nums text-white`}>
      {value}
    </div>
    {sub && <div className="mt-1 text-xs text-zinc-500">{sub}</div>}
  </div>
);

const change = (cur, prev) => {
  if (prev == null) return null;
  if (!prev) return cur > 0 ? 100 : 0;
  return ((cur - prev) / prev) * 100;
};

const plural = (n, w) => `${n ?? 0} ${w}${n === 1 ? '' : 's'}`;

export const ChatterHeadline = ({ s, prev, prevLabel, ratePercent, showEarnings = true, earningsLabel = 'Earnings' }) => {
  const c = change(s?.sales ?? 0, prev?.sales);
  const salesSub = c == null ? 'PPV + tips − chargebacks'
    : `${c >= 0 ? '+' : ''}${c.toFixed(1)}% ${prevLabel || ''}`;
  return (
    <div className={`grid grid-cols-1 ${showEarnings ? 'md:grid-cols-2' : ''} gap-6`}>
      <Card big label="Sales" value={money(s?.sales)} sub={salesSub} icon={TrendingUp} accent="blue" testId="metric-sales" />
      {showEarnings && (
        <Card big label={earningsLabel} value={money(s?.earnings)} sub={`${ratePercent}% of sales`} icon={Wallet} accent="green" testId="metric-earnings" />
      )}
    </div>
  );
};

export const ChatterDetailGrid = ({ s }) => (
  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
    <Card label="PPV revenue" value={money(s?.ppvRevenue)} sub={s?.ppvsSold ? `${s.ppvsSold} sold` : undefined} icon={DollarSign} accent="emerald" testId="metric-ppv-revenue" />
    <Card label="Tips" value={money(s?.tipsRevenue)} sub={plural(s?.tipsCount, 'tip')} icon={Gift} accent="pink" testId="metric-tips" />
    <Card label="Chargebacks" value={money(s?.chargebackAmount)} sub={plural(s?.chargebacksCount, 'chargeback')} icon={RotateCcw} accent="red" testId="metric-chargebacks" />
    <Card label="Messages" value={(s?.messages ?? 0).toLocaleString()} icon={MessageSquare} accent="sky" testId="metric-messages" />
    <Card label="PPV sent" value={(s?.ppvs_sent ?? 0).toLocaleString()} icon={Lock} accent="amber" testId="metric-ppv-sent" />
    <Card label="PPV unlocked" value={(s?.ppvs_unlocked ?? 0).toLocaleString()} sub={`${pct(s?.unlock_ratio)} conversion`} icon={Unlock} accent="violet" testId="metric-ppv-unlocked" />
    <Card label="Active hours" value={(s?.active_hours ?? 0).toFixed(1)} sub={`${money(s?.revenue_per_hour)} / hour`} icon={Clock} accent="cyan" testId="metric-active-hours" />
    <Card label="Avg response" value={respTime(s?.avg_response_ms)} icon={Timer} accent="orange" testId="metric-avg-response" />
  </div>
);
